import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftRight, Landmark, Send } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { MoneyText } from '@/components/ui/MoneyText'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { useToast } from '@/components/ui/Toast'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { listDepartments } from '@/data/departments'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import {
  EXCEPTION_LABELS, createMatch, decideException, listMatches, listOpenExceptions, listOpenRemittances,
  listReceipts, recordReceipt, runCheck, unmatch,
} from '@/data/reconciliation'
import type { OpenMovement, Receipt, ReconException } from '@/data/reconciliation'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { remainingOnReceipt, remainingOnRemittance, suggestMatches } from '@/lib/matching'
import { cn } from '@/lib/cn'
import { lusakaDay, lusakaToday } from '@/lib/periods'
import { amountToNgwee, receiptFormSchema, textSchema } from './schemas'

const newRef = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `x-${Date.now()}-${Math.random().toString(36).slice(2)}`)
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

function Failed({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div>
      <p role="alert" className="text-body text-bad">{errText(error)}</p>
      <Button variant="secondary" size="lg" className="mt-3" onClick={retry}>Try again</Button>
    </div>
  )
}
const linkBtn = 'inline-flex min-h-[44px] shrink-0 items-center rounded-control px-3 text-body font-medium hover:bg-neutral-tint'

/** The title of one side of the match: which step it is, what it holds, and how many. */
function PanelTitle({ step, title, count }: { step: string; title: string; count: number | null }) {
  return (
    <div className="min-w-0">
      <p className="text-micro font-semibold uppercase tracking-wide text-muted">{step}</p>
      <h3 className="flex flex-wrap items-center gap-2 text-card font-semibold text-ink">
        {title}
        {count !== null && <Badge tone={count > 0 ? 'info' : 'neutral'}>{count}</Badge>}
      </h3>
    </div>
  )
}

/** Central treasury: record what arrived, match it to what departments declared, work exceptions. */
export function ReconcilePage() {
  const profile = useProfile()
  const canAct = profile.isAdmin
  const qc = useQueryClient()
  const toast = useToast()
  const today = lusakaToday()

  const movementsQ = useQuery({ queryKey: ['open-remittances'], queryFn: listOpenRemittances })
  const receiptsQ = useQuery({ queryKey: ['open-receipts'], queryFn: () => listReceipts({ onlyOpen: true }) })
  const exceptionsQ = useQuery({ queryKey: ['exceptions'], queryFn: () => listOpenExceptions() })
  const deptsQ = useQuery({ queryKey: ['departments', 'with-retired'], queryFn: () => listDepartments({ includeRetired: true }) })

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pickedId, setPickedId] = useState<string | null>(null) // the received money chosen to match

  const movements = movementsQ.data ?? []
  const receipts = receiptsQ.data ?? []
  const selected = movements.find((m) => m.id === selectedId) ?? null
  const suggestions = selected ? suggestMatches(selected, receipts) : []
  const suggestionOf = new Map(suggestions.map((s) => [s.receipt.id, s]))
  // With a remittance chosen, show the usable receipts best-first; the rest stay listed below.
  const ordered = selected
    ? [...suggestions.map((s) => s.receipt), ...receipts.filter((r) => !suggestionOf.has(r.id))]
    : receipts
  const picked = selected ? receipts.find((r) => r.id === pickedId && suggestionOf.has(r.id)) ?? null : null

  const matchesQ = useQuery({
    queryKey: ['matches', selectedId],
    queryFn: () => listMatches({ movementId: selectedId as string }),
    enabled: selectedId !== null,
  })

  const refresh = async () => {
    for (const k of ['open-remittances', 'open-receipts', 'matches', 'exceptions', 'waiting-budgets']) {
      await qc.invalidateQueries({ queryKey: [k] })
    }
    await qc.invalidateQueries({ queryKey: ['remittances'] })
  }

  // ---------------- record money received
  const [recOpen, setRecOpen] = useState(false)
  const [rDate, setRDate] = useState(today)
  const [rAmount, setRAmount] = useState('')
  const [rMethod, setRMethod] = useState<PaymentMethod>('bank_transfer')
  const [rRef, setRRef] = useState('')
  const [rLine, setRLine] = useState('')
  const [rDept, setRDept] = useState('')
  const [rNote, setRNote] = useState('')
  const [rErrors, setRErrors] = useState<Record<string, string>>({})
  const [rError, setRError] = useState('')
  const recordRef = useRef(newRef())
  const recordM = useMutation({ mutationFn: (v: { input: Parameters<typeof recordReceipt>[0]; ref: string }) => recordReceipt(v.input, v.ref) })

  const openRecord = () => {
    setRDate(today); setRAmount(''); setRMethod('bank_transfer'); setRRef(''); setRLine(''); setRDept(''); setRNote('')
    setRErrors({}); setRError(''); recordRef.current = newRef(); setRecOpen(true)
  }
  const submitRecord = async (e: FormEvent) => {
    e.preventDefault()
    if (recordM.isPending) return
    setRError('')
    const parsed = receiptFormSchema.safeParse({ receivedOn: rDate, amount: rAmount, method: rMethod, bankRef: rRef, statementLineRef: rLine, departmentId: rDept, note: rNote })
    const errs: Record<string, string> = {}
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
    if (parsed.success && parsed.data.receivedOn > today) errs.receivedOn = 'The date cannot be in the future'
    setRErrors(errs)
    if (!parsed.success || Object.keys(errs).length > 0) return
    const v = parsed.data
    try {
      await recordM.mutateAsync({
        ref: recordRef.current,
        input: {
          amountNgwee: amountToNgwee(v.amount) as number, method: v.method, receivedOn: v.receivedOn, bankRef: v.bankRef,
          statementLineRef: v.statementLineRef, departmentId: v.departmentId || null, note: v.note,
        },
      })
      await refresh()
      setRecOpen(false)
      toast.success('The money received was recorded.')
    } catch (err) {
      setRError(errText(err))
    }
  }

  // ---------------- match
  const [matchWith, setMatchWith] = useState<Receipt | null>(null)
  const [mAmount, setMAmount] = useState('')
  const [mError, setMError] = useState('')
  const matchM = useMutation({ mutationFn: createMatch })

  const openMatch = (r: Receipt) => {
    if (!selected) return
    const max = Math.min(remainingOnRemittance(selected), remainingOnReceipt(r))
    setMAmount((max % 100 === 0 ? String(max / 100) : (max / 100).toFixed(2)))
    setMError('')
    setMatchWith(r)
  }
  const submitMatch = async (e: FormEvent) => {
    e.preventDefault()
    if (!selected || !matchWith || matchM.isPending) return
    const ngwee = amountToNgwee(mAmount)
    if (ngwee === null) return setMError('Enter an amount like 500 or 500.00')
    setMError('')
    try {
      await matchM.mutateAsync({ movementId: selected.id, ackId: matchWith.id, departmentId: selected.departmentId, amountNgwee: ngwee })
      await refresh()
      setMatchWith(null)
      setPickedId(null)
      toast.success(`Matched ${formatKwacha(ngwee)} to ${selected.departmentName}.`)
    } catch (err) {
      setMError(errText(err))
      void refresh()
    }
  }

  // ---------------- undo a match / close an exception: both need words
  const [textFor, setTextFor] = useState<{ kind: 'unmatch'; matchId: string; amount: number } | { kind: 'close'; ex: ReconException; to: 'resolved' | 'waived' } | null>(null)
  const [text, setText] = useState('')
  const [textError, setTextError] = useState('')
  const [textBusy, setTextBusy] = useState(false)

  const openText = (t: NonNullable<typeof textFor>) => { setText(''); setTextError(''); setTextFor(t) }
  const submitText = async (e: FormEvent) => {
    e.preventDefault()
    if (!textFor || textBusy) return
    const p = textSchema(textFor.kind === 'unmatch' ? 'Give a reason' : 'Write what was found or decided').safeParse({ text })
    if (!p.success) return setTextError(p.error.issues[0].message)
    setTextBusy(true)
    setTextError('')
    try {
      if (textFor.kind === 'unmatch') await unmatch(textFor.matchId, p.data.text)
      else await decideException(textFor.ex.id, textFor.to, p.data.text)
      await refresh()
      const done = textFor.kind === 'unmatch' ? 'The match was undone. Both sides are open again.' : 'The exception was closed.'
      toast.success(done)
      setTextFor(null)
    } catch (err) {
      setTextError(errText(err))
      void refresh()
    } finally {
      setTextBusy(false)
    }
  }

  // ---------------- exceptions + check
  const [checking, setChecking] = useState(false)
  const [checkedAt, setCheckedAt] = useState<Date | null>(null)
  const [actionError, setActionError] = useState('')
  const check = async () => {
    setChecking(true)
    setActionError('')
    try {
      await runCheck()
      await refresh()
      setCheckedAt(new Date())
      toast.success('Checked. The list below is up to date.')
    } catch (err) {
      setActionError(errText(err))
    } finally {
      setChecking(false)
    }
  }
  const investigate = async (ex: ReconException) => {
    setActionError('')
    try {
      await decideException(ex.id, 'investigating')
      await refresh()
    } catch (err) {
      setActionError(errText(err))
    }
  }

  const deptOptions = [{ value: '', label: 'Not claimed yet' }, ...(deptsQ.data ?? []).filter((d) => !d.is_central).map((d) => ({ value: d.id, label: d.name }))]

  // What the match sheet is about to do, in figures (display only: the database still decides what is allowed).
  const matchingNgwee = amountToNgwee(mAmount)
  const gap = selected && matchWith ? remainingOnRemittance(selected) - remainingOnReceipt(matchWith) : 0

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Reconcile"
        description={canAct ? 'Pick a remittance on the left, then match the money you received on the right.' : 'What departments say they sent, and what central treasury has received. You can look but not change it.'}
      />

      <div className="grid items-stretch gap-4 lg:grid-cols-[1fr_auto_1fr]">
        <Card className="flex h-full flex-col">
          <CardHeader>
            <PanelTitle step="Step 1" title="Sent by departments, not yet confirmed" count={movementsQ.isSuccess ? movements.length : null} />
          </CardHeader>
          <CardBody className="flex-1">
            {movementsQ.isPending ? <SkeletonRows rows={3} /> : movementsQ.isError ? <Failed error={movementsQ.error} retry={() => void movementsQ.refetch()} /> : movements.length === 0 ? (
              <EmptyState icon={Send} title="Everything sent has been confirmed" description="New remittances appear here when a department records one." />
            ) : (
              <ul className="flex max-h-[32rem] flex-col gap-2 overflow-y-auto" aria-label="Remittances to match">
                {movements.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(m.id === selectedId ? null : m.id)
                        setPickedId(null)
                      }}
                      aria-pressed={m.id === selectedId}
                      className={cn(
                        'w-full rounded-control border p-3 text-left transition-colors',
                        m.id === selectedId ? 'border-brand bg-brand-tint' : 'border-line bg-surface hover:border-brand',
                        selected !== null && m.id !== selectedId && 'opacity-60',
                      )}
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block font-medium text-ink">{m.departmentName}</span>
                          <span className="block text-body text-muted">
                            {formatDay(m.movedOn)} · {METHOD_LABELS[m.method]}
                            {m.method === 'cash' ? (m.handedTo ? `, to ${m.handedTo}` : '') : m.bankRef ? `, ref ${m.bankRef}` : ''}
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          <span className="block font-semibold tabular-nums text-ink">{formatKwacha(m.declaredNgwee)}</span>
                          {m.matchedNgwee > 0 && <span className="block text-label text-muted">{formatKwacha(remainingOnRemittance(m))} still to find</span>}
                          {m.status === 'disputed' && <Badge tone="danger">Under query</Badge>}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* The join between the two sides: what is chosen on each, how they compare, and the one button that matches them. */}
        <div className="flex flex-col items-center justify-center gap-2 rounded-card border border-dashed border-line-strong bg-page p-3 text-center lg:w-52" aria-label="The match" role="group">
          <ArrowLeftRight size={20} className="text-muted" aria-hidden />
          {!selected ? (
            <p className="text-label text-muted">Choose what was sent, then the money that arrived.</p>
          ) : !picked ? (
            <p className="text-label text-muted">Now choose the money received that belongs to {selected.departmentName}.</p>
          ) : (
            <>
              <p className="text-label text-ink">
                {selected.departmentName} sent <strong className="tabular-nums">{formatKwacha(remainingOnRemittance(selected))}</strong>
              </p>
              <p className="text-label text-ink">
                You received <strong className="tabular-nums">{formatKwacha(remainingOnReceipt(picked))}</strong>
              </p>
              {remainingOnRemittance(selected) === remainingOnReceipt(picked) ? (
                <p className="text-label font-medium text-ok">The amounts agree</p>
              ) : (
                <p className="text-label font-medium text-bad">
                  Difference {formatKwacha(Math.abs(remainingOnRemittance(selected) - remainingOnReceipt(picked)))}
                </p>
              )}
              {canAct && (
                <Button size="sm" onClick={() => openMatch(picked)}>
                  Match these two
                </Button>
              )}
            </>
          )}
        </div>

        <Card className="flex h-full flex-col">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <PanelTitle step="Step 2" title="Received by central treasury" count={receiptsQ.isSuccess ? receipts.length : null} />
              {canAct && <Button onClick={openRecord}>Record money received</Button>}
            </div>
          </CardHeader>
          <CardBody className="flex-1">
            {receiptsQ.isPending ? <SkeletonRows rows={3} /> : receiptsQ.isError ? <Failed error={receiptsQ.error} retry={() => void receiptsQ.refetch()} /> : receipts.length === 0 ? (
              <EmptyState icon={Landmark} title="No unmatched money" description={canAct ? 'Record money when it arrives in the bank or by hand.' : 'Nothing is waiting to be matched.'} />
            ) : (
              <ul className="flex max-h-[32rem] flex-col gap-2 overflow-y-auto" aria-label="Money received">
                {ordered.map((r) => {
                  const s = suggestionOf.get(r.id)
                  const usable = selected !== null && s !== undefined
                  const best = s !== undefined && ordered[0]?.id === r.id
                  return (
                    <li
                      key={r.id}
                      className={cn(
                        'rounded-control border p-3 transition-colors',
                        r.id === pickedId && usable ? 'border-brand bg-brand-tint' : best ? 'border-ok/50 bg-ok-tint' : 'border-line bg-surface',
                        selected !== null && !usable && 'opacity-60',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink">
                            {formatDay(r.receivedOn)} · {METHOD_LABELS[r.method]}
                            {r.bankRef ? `, ref ${r.bankRef}` : ''}
                          </p>
                          <p className="text-body text-muted">{r.departmentName ? `For ${r.departmentName}` : 'Not claimed by any department yet'}</p>
                          {r.note && <p className="text-label text-muted">{r.note}</p>}
                          {s && (
                            <p className="mt-1 flex flex-wrap gap-1">
                              {best && <Badge tone="success">Best match</Badge>}
                              {s.reasons.map((x) => <Badge key={x} tone="success">{x}</Badge>)}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="font-semibold tabular-nums text-ink">{formatKwacha(remainingOnReceipt(r))}</p>
                          {r.allocatedNgwee > 0 && <p className="text-label text-muted">of {formatKwacha(r.receivedNgwee)}</p>}
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {canAct && usable && (
                          <button type="button" aria-pressed={r.id === pickedId} onClick={() => setPickedId(r.id === pickedId ? null : r.id)} className={`${linkBtn} text-brand`}>
                            {r.id === pickedId ? 'Chosen' : 'Choose this one'}
                          </button>
                        )}
                        {canAct && usable && (
                          <button type="button" onClick={() => openMatch(r)} className={`${linkBtn} text-brand`}>
                            Match to {selected.departmentName}
                          </button>
                        )}
                        <OwnerDeleteButton table="cash_movement_ack" id={r.id} what="receipt of money" />
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {selected && (
        <Card>
          <CardHeader title={`Matched so far: ${selected.departmentName}, ${formatKwacha(selected.declaredNgwee)}`} />
          <CardBody>
            {matchesQ.isPending ? <SkeletonRows rows={2} /> : matchesQ.isError ? <Failed error={matchesQ.error} retry={() => void matchesQ.refetch()} /> : (matchesQ.data ?? []).length === 0 ? (
              <p className="text-body text-muted">Nothing matched yet. {formatKwacha(remainingOnRemittance(selected))} to find.</p>
            ) : (
              <ul className="divide-y divide-line">
                {(matchesQ.data ?? []).map((m) => (
                  <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                    <span className="text-body text-ink">{formatKwacha(m.amountNgwee)} matched on {formatDay(lusakaDay(m.matchedAt))}</span>
                    {canAct && <button type="button" className={`${linkBtn} text-bad`} onClick={() => openText({ kind: 'unmatch', matchId: m.id, amount: m.amountNgwee })}>Undo</button>}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-card font-semibold text-ink">Things to look at</h3>
              <p className="mt-0.5 text-body text-muted">
                Every night the system checks for remittances that were never received, amounts that do not agree, partly confirmed money left too long, missing photos and cash held too long.
                {checkedAt ? ` Last checked from here at ${checkedAt.toLocaleTimeString('en-GB', { timeZone: 'Africa/Lusaka', hour: '2-digit', minute: '2-digit' })}.` : ''}
              </p>
            </div>
            {canAct && <Button size="lg" variant="secondary" isLoading={checking} onClick={() => void check()}>Check now</Button>}
          </div>
        </CardHeader>
        <CardBody>
          {actionError && <p role="alert" className="mb-3 text-body text-bad">{actionError}</p>}
          {exceptionsQ.isPending ? <SkeletonRows rows={2} /> : exceptionsQ.isError ? <Failed error={exceptionsQ.error} retry={() => void exceptionsQ.refetch()} /> : (exceptionsQ.data ?? []).length === 0 ? (
            <EmptyState title="Nothing needs attention" description="The check runs every night and you can run it now." />
          ) : (
            <ul className="divide-y divide-line">
              {(exceptionsQ.data ?? []).map((x) => (
                <li key={x.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{EXCEPTION_LABELS[x.kind]}</p>
                      <p className="text-body text-muted">
                        {x.departmentName ?? 'Whole church'}
                        {x.varianceNgwee !== null ? ' · ' : ''}
                        {x.varianceNgwee !== null && <MoneyText ngwee={x.varianceNgwee} strongZero />}
                      </p>
                      {x.deptResponse && <p className="mt-1 rounded-control bg-page px-2 py-1 text-body text-ink">Department says: {x.deptResponse}</p>}
                    </div>
                    {x.state === 'investigating' && <Badge tone="info">Looking into it</Badge>}
                  </div>
                  {canAct && (
                    <div className="flex flex-wrap gap-1">
                      {x.state === 'open' && <button type="button" className={`${linkBtn} text-brand`} onClick={() => void investigate(x)}>Looking into it</button>}
                      <button type="button" className={`${linkBtn} text-brand`} onClick={() => openText({ kind: 'close', ex: x, to: 'resolved' })}>Resolve</button>
                      <button type="button" className={`${linkBtn} text-neutral`} onClick={() => openText({ kind: 'close', ex: x, to: 'waived' })}>Waive</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={recOpen} onClose={() => setRecOpen(false)} title="Record money received">
        <form onSubmit={(e) => void submitRecord(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Date it arrived" type="date" max={today} value={rDate} onChange={(e) => setRDate(e.target.value)} error={rErrors.receivedOn} />
          <Input label="Amount in Kwacha" inputMode="decimal" placeholder="500.00" value={rAmount} onChange={(e) => setRAmount(e.target.value)} error={rErrors.amount} />
          <Select
            label="How did it arrive?"
            value={rMethod}
            onChange={(e) => setRMethod(e.target.value as PaymentMethod)}
            options={(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD_LABELS[m] }))}
          />
          <Input label="Bank or mobile money reference (optional)" value={rRef} onChange={(e) => setRRef(e.target.value)} error={rErrors.bankRef} hint="The same reference the department gave helps match it." />
          <Input label="Statement line (optional)" value={rLine} onChange={(e) => setRLine(e.target.value)} error={rErrors.statementLineRef} />
          <Select label="Which department sent it?" value={rDept} onChange={(e) => setRDept(e.target.value)} options={deptOptions} hint="Leave as not claimed if you do not know. Matching it to a remittance will fill this in." />
          <Input label="Note (optional)" value={rNote} onChange={(e) => setRNote(e.target.value)} error={rErrors.note} />
          {rError && <p role="alert" className="text-body text-bad">{rError}</p>}
          <Button type="submit" size="lg" isLoading={recordM.isPending}>{recordM.isPending ? 'Saving...' : 'Save'}</Button>
        </form>
      </Sheet>

      <Sheet isOpen={matchWith !== null} onClose={() => setMatchWith(null)} title="Match this money">
        <form onSubmit={(e) => void submitMatch(e)} className="flex flex-col gap-4" noValidate>
          {selected && matchWith && (
            <>
              <p className="text-body text-ink">
                {formatKwacha(remainingOnReceipt(matchWith))} received on {formatDay(matchWith.receivedOn)} to {selected.departmentName}'s remittance of {formatKwacha(selected.declaredNgwee)}
                {selected.matchedNgwee > 0 ? ` (${formatKwacha(remainingOnRemittance(selected))} still to find)` : ''}.
              </p>
              <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 rounded-control border border-line bg-page p-3 text-body">
                <dt className="text-muted">Sent, still to find</dt>
                <dd className="text-right font-medium tabular-nums">{formatKwacha(remainingOnRemittance(selected))}</dd>
                <dt className="text-muted">Received, still free</dt>
                <dd className="text-right font-medium tabular-nums">{formatKwacha(remainingOnReceipt(matchWith))}</dd>
                <dt className="text-muted">Difference</dt>
                <dd className={cn('text-right font-medium tabular-nums', gap !== 0 && 'text-bad')}>
                  {gap === 0 ? 'None' : gap > 0 ? `${formatKwacha(gap)} less received than sent` : `${formatKwacha(-gap)} more received than sent`}
                </dd>
                {matchingNgwee !== null && (
                  <>
                    <dt className="text-muted">Matching now</dt>
                    <dd className="text-right font-semibold tabular-nums">{formatKwacha(matchingNgwee)}</dd>
                  </>
                )}
              </dl>
            </>
          )}
          <Input label="Amount to match in Kwacha" inputMode="decimal" value={mAmount} onChange={(e) => setMAmount(e.target.value)} hint="You can match part of it. The rest stays open." error={mError} />
          <Button type="submit" size="lg" isLoading={matchM.isPending}>{matchM.isPending ? 'Matching...' : 'Match'}</Button>
        </form>
      </Sheet>

      <Sheet
        isOpen={textFor !== null}
        onClose={() => setTextFor(null)}
        title={textFor?.kind === 'unmatch' ? 'Undo this match?' : textFor?.to === 'waived' ? 'Waive this exception' : 'Resolve this exception'}
      >
        <form onSubmit={(e) => void submitText(e)} className="flex flex-col gap-4" noValidate>
          <p className="text-body text-ink">
            {textFor?.kind === 'unmatch' ? `${formatKwacha(textFor.amount)} goes back to being unmatched. The history is kept.` : 'This stays on the record with your note.'}
          </p>
          <Textarea label={textFor?.kind === 'unmatch' ? 'Why?' : 'What was found or decided?'} value={text} onChange={(e) => setText(e.target.value)} error={textError} rows={3} />
          <Button type="submit" size="lg" variant={textFor?.kind === 'unmatch' ? 'danger' : 'primary'} isLoading={textBusy}>
            {textFor?.kind === 'unmatch' ? 'Undo match' : textFor?.to === 'waived' ? 'Waive' : 'Resolve'}
          </Button>
        </form>
      </Sheet>
    </div>
  )
}

export type { OpenMovement }
